import { components, useApi } from "@appretio/api";
import { useAuth0 } from "@appretio/auth";
import { useQuery } from "@tanstack/react-query";

export type UserOrganization = components["schemas"]["UserOrganization"];

/**
 * Fetches the Auth0 Organizations (tenants) the current user belongs to.
 * Drives the UserMenu tenant switcher: each entry's `id` is the Auth0
 * organization id, and picking one triggers a full-page org-scoped
 * `loginWithRedirect({ authorizationParams: { organization } })` that
 * re-mounts the app scoped to that org (see `TenantProvider.switchOrg`).
 *
 * Cached for the session — org membership only changes at the IdP, not
 * during a browser session. While pending, `data` is `undefined` and
 * `isLoading` is `true`; the selector should render a loading state.
 */
export function useUserOrganizations() {
  const apiClient = useApi();
  const { isAuthenticated } = useAuth0();

  return useQuery({
    queryKey: ["userOrganizations"],
    // Only after login: TenantProvider mounts above AuthGuard, so an
    // unauthenticated fetch here would trip the API client's reauth redirect
    // and bypass the in-app login screen.
    enabled: isAuthenticated,
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to load your organizations"
    },
    queryFn: async () => {
      const response = await apiClient.GET("/v2/auth/me/organizations", {});
      return response.data?.result ?? [];
    }
  });
}
